import { useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, X, Loader2, CheckCircle2, ArrowLeft, Brain, Filter, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { CEFR_LEVELS, COMMON_LANGUAGES } from "@/types/candidate";
import { SearchResultsList } from "./AdvancedSearchPanel/SearchResultsList";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Skill {
	id: string;
	name: string;
	isSoft?: boolean;
}

interface Experience {
	id: string;
	name: string;
	category: string;
}

interface LanguageOverride {
	languageCode: string;  // ISO 639-1
	minCefrLevel: number;  // 0=any, 1=A1, 2=A2, 3=B1, 4=B2, 5=C1, 6=C2, 7=native
	isMandatory: boolean;
	excluded: boolean;
}

interface AdvancedSearchPanelProps {
	jobId: string;
	onAddMatches?: (candidates: string[]) => void;
	onClose?: () => void;
}

export function AdvancedSearchPanel({ jobId, onAddMatches, onClose }: AdvancedSearchPanelProps) {
	const { t } = useTranslation();
	const progressEndRef = useRef<HTMLDivElement>(null);

	// Skills state
	const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
	const [requiredSkills, setRequiredSkills] = useState<Skill[]>([]);
	const [excludedSkills, setExcludedSkills] = useState<Skill[]>([]);

	// Experiences state
	const [availableExperiences, setAvailableExperiences] = useState<Experience[]>([]);
	const [requiredExperiences, setRequiredExperiences] = useState<Experience[]>([]);
	const [excludedExperiences, setExcludedExperiences] = useState<Experience[]>([]);

	// Track IDs originally loaded from the job profile — used to detect user-added items
	const originalSkillIds = useRef<Set<string>>(new Set());
	const originalExperienceIds = useRef<Set<string>>(new Set());

	// Search parameters
	const [excludeSoftSkills, setExcludeSoftSkills] = useState(true);
	const [flexibilityLevel, setFlexibilityLevel] = useState<'strict' | 'normal' | 'flexible'>('normal');
	const [languageOverrides, setLanguageOverrides] = useState<LanguageOverride[]>([]);
	const [newLanguageCode, setNewLanguageCode] = useState('');

	// Drag state
	const [draggedItem, setDraggedItem] = useState<{ id: string; type: 'skill' | 'experience' } | null>(null);
	const [dragOverZone, setDragOverZone] = useState<string | null>(null);

	// Search state
	const [isSearching, setIsSearching] = useState(false);
	const [searchResults, setSearchResults] = useState<any>(null);
	const [searchProgress, setSearchProgress] = useState<Array<{
		step: string;
		status: 'pending' | 'running' | 'completed' | 'error';
		data?: any;
	}>>([]);

	// View state: config or results
	const [currentView, setCurrentView] = useState<'config' | 'results'>('config');

	// Job details state
	const [jobTitle, setJobTitle] = useState<string>('');
	const [companyName, setCompanyName] = useState<string>('');

	// Quick add state
	const [skillSearchQuery, setSkillSearchQuery] = useState('');
	const [experienceSearchQuery, setExperienceSearchQuery] = useState('');
	const [skillSearchResults, setSkillSearchResults] = useState<Skill[]>([]);
	const [experienceSearchResults, setExperienceSearchResults] = useState<Experience[]>([]);
	const [isSearchingSkills, setIsSearchingSkills] = useState(false);
	const [isSearchingExperiences, setIsSearchingExperiences] = useState(false);
	const [recentlyAddedSkills, setRecentlyAddedSkills] = useState<Set<string>>(new Set());
	const [recentlyAddedExperiences, setRecentlyAddedExperiences] = useState<Set<string>>(new Set());

	// Load initial job details, skills and experiences
	useEffect(() => {
		loadJobDetails();
		loadJobSkillsAndExperiences();
	}, [jobId]);

	// Set up realtime channel for search progress
	useEffect(() => {
		let channel: any = null;

		const setupChannel = async () => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) return;

			const channelId = `job_search:${jobId}:${user.id}`;
			channel = supabase.channel(channelId);

			channel
				.on(
					'broadcast',
					{ event: 'search_progress' },
					(payload: any) => {
						const event = payload.payload;

						if (event.step === 'complete') {
								setSearchResults(event.result);
							setIsSearching(false);
						} else if (event.step === 'error') {
							toast({
								title: t("errors.error"),
								description: event.message || t("advancedSearch.toasts.searchError"),
								variant: "destructive",
							});
							setIsSearching(false);
						} else {
							setSearchProgress(prev => {
								const existing = prev.find(p => p.step === event.step);
								if (existing) {
									return prev.map(p => p.step === event.step ? event : p);
								} else {
									return [...prev, event];
								}
							});
						}
					}
				)
				.subscribe();
		};

		setupChannel();

		return () => {
			if (channel) {
				supabase.removeChannel(channel);
			}
		};
	}, [jobId, t]);

	// Auto-scroll to bottom when progress updates
	useEffect(() => {
		if (searchProgress.length > 0) {
			progressEndRef.current?.scrollIntoView({ behavior: 'smooth' });
		}
	}, [searchProgress]);

	// Auto-switch to results view when search completes
	useEffect(() => {
		if (searchResults) {
			setCurrentView('results');
		}
	}, [searchResults]);

	// Handle soft skills toggle
	useEffect(() => {
		if (excludeSoftSkills) {
			const softSkills = availableSkills.filter(s => s.isSoft);
			if (softSkills.length > 0) {
				setAvailableSkills(availableSkills.filter(s => !s.isSoft));
				setExcludedSkills([...excludedSkills, ...softSkills]);
			}
		} else {
			const softSkills = excludedSkills.filter(s => s.isSoft);
			if (softSkills.length > 0) {
				setExcludedSkills(excludedSkills.filter(s => !s.isSoft));
				setAvailableSkills([...availableSkills, ...softSkills]);
			}
		}
	}, [excludeSoftSkills]);

	const loadJobDetails = async () => {
		try {
			const { data: jobData, error: jobError } = await supabase
				.from('jobs')
				.select('title, companies(name)')
				.eq('id', jobId)
				.single();

			if (jobError) throw jobError;

			if (jobData) {
				setJobTitle(jobData.title || '');
				// @ts-ignore - Supabase returns companies as array but it's a single relation
				setCompanyName(jobData.companies?.name || '');
			}
		} catch (error) {
			console.error('Error loading job details:', error);
		}
	};

	const loadJobSkillsAndExperiences = async () => {
		try {
			const { data: jobSkillsData, error: skillsError } = await supabase
				.from('job_skills')
				.select(`
					normalized_skill_id,
					normalized_skills!inner(id, name, is_soft_skill)
				`)
				.eq('job_id', jobId);

			if (skillsError) throw skillsError;

			const uniqueSkills = jobSkillsData?.reduce((acc: any[], row: any) => {
				if (!acc.find(item => item.normalized_skills.id === row.normalized_skills.id))
					acc.push(row);
				return acc;
			}, []) || [];

			const skills = uniqueSkills.map((row: any) => ({
				id: row.normalized_skills.id,
				name: row.normalized_skills.name,
				isSoft: row.normalized_skills.is_soft_skill
			})) || [];

			originalSkillIds.current = new Set(skills.map(s => s.id));

			const regularSkills = skills.filter(s => !s.isSoft);
			const softSkills = skills.filter(s => s.isSoft);

			setAvailableSkills(regularSkills);
			setExcludedSkills(softSkills);

			const { data: jobExpData, error: expError } = await supabase
				.from('job_experiences')
				.select(`
					normalized_experience_id,
					normalized_experiences!inner(id, name, category)
				`)
				.eq('job_id', jobId);

			if (expError) throw expError;

			const uniqueExperiences = jobExpData?.reduce((acc: any[], row: any) => {
				if (!acc.find(item => item.normalized_experiences.id === row.normalized_experiences.id))
					acc.push(row);
				return acc;
			}, []) || [];

			const experiences = uniqueExperiences.map((row: any) => ({
				id: row.normalized_experiences.id,
				name: row.normalized_experiences.name,
				category: row.normalized_experiences.category
			})) || [];

			originalExperienceIds.current = new Set(experiences.map(e => e.id));

			setAvailableExperiences(experiences);

			const { data: jobLangsData } = await supabase
				.from('job_languages')
				.select('language_code, min_cefr_level, is_mandatory')
				.eq('job_id', jobId);

			if (jobLangsData) {
				setLanguageOverrides(jobLangsData.map((row: any) => ({
					languageCode: row.language_code,
					minCefrLevel: row.min_cefr_level,
					isMandatory: row.is_mandatory,
					excluded: false,
				})));
			}
		} catch (error) {
			console.error('Error loading job skills/experiences:', error);
			toast({
				title: t("errors.error"),
				description: t("advancedSearch.toasts.loadError"),
				variant: "destructive",
			});
		}
	};

	// Quick search for skills
	useEffect(() => {
		if (skillSearchQuery.trim().length >= 2) {
			const delaySearch = setTimeout(() => {
				searchSkills(skillSearchQuery);
			}, 300);
			return () => clearTimeout(delaySearch);
		} else {
			setSkillSearchResults([]);
		}
	}, [skillSearchQuery]);

	// Quick search for experiences
	useEffect(() => {
		if (experienceSearchQuery.trim().length >= 2) {
			const delaySearch = setTimeout(() => {
				searchExperiences(experienceSearchQuery);
			}, 300);
			return () => clearTimeout(delaySearch);
		} else {
			setExperienceSearchResults([]);
		}
	}, [experienceSearchQuery]);

	const searchSkills = async (query: string) => {
		setIsSearchingSkills(true);
		try {
			const existingIds = [...availableSkills, ...requiredSkills, ...excludedSkills].map(s => s.id);

			const { data, error } = await supabase
				.from('normalized_skills')
				.select('id, name, is_soft_skill')
				.ilike('name', `%${query}%`)
				.not('id', 'in', existingIds.length > 0 ? `(${existingIds.join(',')})` : '()')
				.limit(10);

			if (error) throw error;

			setSkillSearchResults(data?.map(item => ({
				id: item.id,
				name: item.name,
				isSoft: item.is_soft_skill
			})) || []);
		} catch (error) {
			console.error('Error searching skills:', error);
		} finally {
			setIsSearchingSkills(false);
		}
	};

	const searchExperiences = async (query: string) => {
		setIsSearchingExperiences(true);
		try {
			const existingIds = [...availableExperiences, ...requiredExperiences, ...excludedExperiences].map(e => e.id);

			const { data, error } = await supabase
				.from('normalized_experiences')
				.select('id, name, category')
				.ilike('name', `%${query}%`)
				.not('id', 'in', existingIds.length > 0 ? `(${existingIds.join(',')})` : '()')
				.limit(10);

			if (error) throw error;

			setExperienceSearchResults(data?.map(item => ({
				id: item.id,
				name: item.name,
				category: item.category
			})) || []);
		} catch (error) {
			console.error('Error searching experiences:', error);
		} finally {
			setIsSearchingExperiences(false);
		}
	};

	// State cycling functions
	const cycleSkillState = (skillId: string) => {
		const inAvailable = availableSkills.find(s => s.id === skillId);
		const inRequired = requiredSkills.find(s => s.id === skillId);
		const inExcluded = excludedSkills.find(s => s.id === skillId);

		if (inAvailable) {
			setAvailableSkills(availableSkills.filter(s => s.id !== skillId));
			setRequiredSkills([...requiredSkills, inAvailable]);
		} else if (inRequired) {
			setRequiredSkills(requiredSkills.filter(s => s.id !== skillId));
			setExcludedSkills([...excludedSkills, inRequired]);
		} else if (inExcluded) {
			setExcludedSkills(excludedSkills.filter(s => s.id !== skillId));
			setAvailableSkills([...availableSkills, inExcluded]);
		}
	};

	const cycleExperienceState = (expId: string) => {
		const inAvailable = availableExperiences.find(e => e.id === expId);
		const inRequired = requiredExperiences.find(e => e.id === expId);
		const inExcluded = excludedExperiences.find(e => e.id === expId);

		if (inAvailable) {
			setAvailableExperiences(availableExperiences.filter(e => e.id !== expId));
			setRequiredExperiences([...requiredExperiences, inAvailable]);
		} else if (inRequired) {
			setRequiredExperiences(requiredExperiences.filter(e => e.id !== expId));
			setExcludedExperiences([...excludedExperiences, inRequired]);
		} else if (inExcluded) {
			setExcludedExperiences(excludedExperiences.filter(e => e.id !== expId));
			setAvailableExperiences([...availableExperiences, inExcluded]);
		}
	};

	const removeSkill = (skillId: string) => {
		setAvailableSkills(availableSkills.filter(s => s.id !== skillId));
		setRequiredSkills(requiredSkills.filter(s => s.id !== skillId));
		setExcludedSkills(excludedSkills.filter(s => s.id !== skillId));
	};

	const removeExperience = (expId: string) => {
		setAvailableExperiences(availableExperiences.filter(e => e.id !== expId));
		setRequiredExperiences(requiredExperiences.filter(e => e.id !== expId));
		setExcludedExperiences(excludedExperiences.filter(e => e.id !== expId));
	};

	const moveSkillToState = (skillId: string, targetState: 'required' | 'searched' | 'excluded') => {
		const allLists = { available: availableSkills, required: requiredSkills, excluded: excludedSkills };
		let skill: Skill | undefined;
		for (const [, list] of Object.entries(allLists)) {
			const found = list.find(s => s.id === skillId);
			if (found) { skill = found; break; }
		}
		if (!skill) return;

		const newAvailable = availableSkills.filter(s => s.id !== skillId);
		const newRequired = requiredSkills.filter(s => s.id !== skillId);
		const newExcluded = excludedSkills.filter(s => s.id !== skillId);

		if (targetState === 'searched') newAvailable.push(skill);
		else if (targetState === 'required') newRequired.push(skill);
		else newExcluded.push(skill);

		setAvailableSkills(newAvailable);
		setRequiredSkills(newRequired);
		setExcludedSkills(newExcluded);
	};

	const moveExperienceToState = (expId: string, targetState: 'required' | 'searched' | 'excluded') => {
		const allLists = { available: availableExperiences, required: requiredExperiences, excluded: excludedExperiences };
		let exp: Experience | undefined;
		for (const [, list] of Object.entries(allLists)) {
			const found = list.find(e => e.id === expId);
			if (found) { exp = found; break; }
		}
		if (!exp) return;

		const newAvailable = availableExperiences.filter(e => e.id !== expId);
		const newRequired = requiredExperiences.filter(e => e.id !== expId);
		const newExcluded = excludedExperiences.filter(e => e.id !== expId);

		if (targetState === 'searched') newAvailable.push(exp);
		else if (targetState === 'required') newRequired.push(exp);
		else newExcluded.push(exp);

		setAvailableExperiences(newAvailable);
		setRequiredExperiences(newRequired);
		setExcludedExperiences(newExcluded);
	};

	const handleDragStart = (e: React.DragEvent, id: string, type: 'skill' | 'experience') => {
		setDraggedItem({ id, type });
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragEnd = () => {
		setDraggedItem(null);
		setDragOverZone(null);
	};

	const handleDropOnZone = (e: React.DragEvent, targetState: 'required' | 'searched' | 'excluded', zoneType: 'skill' | 'experience') => {
		e.preventDefault();
		setDragOverZone(null);
		if (!draggedItem || draggedItem.type !== zoneType) return;

		if (draggedItem.type === 'skill')
			moveSkillToState(draggedItem.id, targetState);
		else
			moveExperienceToState(draggedItem.id, targetState);

		setDraggedItem(null);
	};

	const addSkillToSection = (skill: Skill, section: 'available' | 'required' | 'excluded') => {
		if (section === 'available')
			setAvailableSkills([...availableSkills, skill]);
		else if (section === 'required')
			setRequiredSkills([...requiredSkills, skill]);
		else
			setExcludedSkills([...excludedSkills, skill]);

		setRecentlyAddedSkills(new Set([...recentlyAddedSkills, skill.id]));
		setTimeout(() => {
			setRecentlyAddedSkills((prev) => {
				const newSet = new Set(prev);
				newSet.delete(skill.id);
				return newSet;
			});
		}, 1000);

		setSkillSearchResults(skillSearchResults.filter(s => s.id !== skill.id));
	};

	const addExperienceToSection = (exp: Experience, section: 'available' | 'required' | 'excluded') => {
		if (section === 'available')
			setAvailableExperiences([...availableExperiences, exp]);
		else if (section === 'required')
			setRequiredExperiences([...requiredExperiences, exp]);
		else
			setExcludedExperiences([...excludedExperiences, exp]);

		setRecentlyAddedExperiences(new Set([...recentlyAddedExperiences, exp.id]));
		setTimeout(() => {
			setRecentlyAddedExperiences((prev) => {
				const newSet = new Set(prev);
				newSet.delete(exp.id);
				return newSet;
			});
		}, 1000);

		setExperienceSearchResults(experienceSearchResults.filter(e => e.id !== exp.id));
	};

	const executeSearch = async () => {
		setIsSearching(true);
		setSearchResults(null);
		setSearchProgress([]);
		setCurrentView('results');

		try {
			const params = new URLSearchParams({
				jobId,
				flexibilityLevel,
				includeSoftSkills: (!excludeSoftSkills).toString(),
			});

			if (requiredSkills.length > 0)
				params.append('mandatorySkillIds', requiredSkills.map(s => s.id).join(','));
			if (requiredExperiences.length > 0)
				params.append('mandatoryExperienceIds', requiredExperiences.map(e => e.id).join(','));
			if (excludedSkills.length > 0)
				params.append('excludedSkillIds', excludedSkills.map(s => s.id).join(','));
			if (excludedExperiences.length > 0)
				params.append('excludedExperienceIds', excludedExperiences.map(e => e.id).join(','));

			// Skills/experiences the user added from outside the job's original profile
			const includedSkillIds = [...availableSkills, ...requiredSkills]
				.filter(s => !originalSkillIds.current.has(s.id))
				.map(s => s.id);
			const includedExperienceIds = [...availableExperiences, ...requiredExperiences]
				.filter(e => !originalExperienceIds.current.has(e.id))
				.map(e => e.id);
			if (includedSkillIds.length > 0)
				params.append('includedSkillIds', includedSkillIds.join(','));
			if (includedExperienceIds.length > 0)
				params.append('includedExperienceIds', includedExperienceIds.join(','));

			const activeLanguages = languageOverrides.filter(l => !l.excluded);
			if (activeLanguages.length > 0)
				params.append('languageOverrides', activeLanguages.map(l => `${l.languageCode}:${l.minCefrLevel}:${l.isMandatory}`).join(','));

			supabase.functions.invoke(
				`digger-intelligence?command=match-job-with-candidates-fast&${params.toString()}`, {
					method: 'GET',
				}
			);
		} catch (error) {
			console.error('Error executing search:', error);
			toast({
				title: t("errors.error"),
				description: t("advancedSearch.toasts.searchError"),
				variant: "destructive",
			});
			setIsSearching(false);
			setCurrentView('config');
		}
	};

	const getStateColor = (state: 'required' | 'searched' | 'excluded') => {
		switch (state) {
			case 'required': return 'bg-emerald-500';
			case 'searched': return 'bg-blue-500';
			case 'excluded': return 'bg-red-400';
		}
	};

	const getChipClasses = (state: 'required' | 'searched' | 'excluded') => {
		switch (state) {
			case 'required':
				return 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-300';
			case 'searched':
				return 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 text-blue-800 dark:text-blue-300';
			case 'excluded':
				return 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 text-red-400 dark:text-red-500 line-through opacity-60';
		}
	};

	const renderDropZone = (
		state: 'required' | 'searched' | 'excluded',
		items: any[],
		type: 'skill' | 'experience',
		label: string,
		labelColor: string,
		showSeparator: boolean
	) => {
		const zoneKey = `${type}-${state}`;
		const isOver = dragOverZone === zoneKey && draggedItem?.type === type;
		const borderHighlight = state === 'required'
			? 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20'
			: state === 'searched'
				? 'border-blue-400 bg-blue-50/50 dark:bg-blue-950/20'
				: 'border-red-400 bg-red-50/50 dark:bg-red-950/20';

		return (
			<div
				key={state}
				onDragOver={(e) => { e.preventDefault(); setDragOverZone(zoneKey); }}
				onDragLeave={() => setDragOverZone(null)}
				onDrop={(e) => handleDropOnZone(e, state, type)}
				className={`rounded-sm p-2 transition-all min-h-[36px] ${isOver ? `border-2 border-dashed ${borderHighlight}` : 'border-2 border-transparent'}`}
			>
				{showSeparator && <div className="border-t border-border/50 dark:border-border/30 -mt-2 mb-2" />}
				<p className={`text-xs font-medium ${labelColor} mb-1.5`}>{label}</p>
				<div className="flex flex-wrap gap-1.5">
					{items.map(item => renderChip(item, type))}
					{items.length === 0 && isOver && (
						<p className="text-xs text-muted-foreground/50 italic">{t("advancedSearch.legend.dropHere")}</p>
					)}
				</div>
			</div>
		);
	};

	const renderSkillChips = () => {
		const allSkills = [
			...requiredSkills.map(s => ({ ...s, state: 'required' as const })),
			...availableSkills.map(s => ({ ...s, state: 'searched' as const })),
			...excludedSkills.map(s => ({ ...s, state: 'excluded' as const })),
		];

		const groups = {
			required: allSkills.filter(s => s.state === 'required'),
			searched: allSkills.filter(s => s.state === 'searched'),
			excluded: allSkills.filter(s => s.state === 'excluded'),
		};

		return (
			<div className="space-y-1">
				{renderDropZone(
					'required', groups.required, 'skill',
					t("advancedSearch.skills.required"), 'text-emerald-600 dark:text-emerald-400', false
				)}
				{renderDropZone(
					'searched', groups.searched, 'skill',
					t("advancedSearch.skills.searched"), 'text-blue-500 dark:text-blue-400', true
				)}
				{renderDropZone(
					'excluded', groups.excluded, 'skill',
					t("advancedSearch.skills.excluded"), 'text-red-500 dark:text-red-400', true
				)}
			</div>
		);
	};

	const renderExperienceChips = () => {
		const allExperiences = [
			...requiredExperiences.map(e => ({ ...e, state: 'required' as const })),
			...availableExperiences.map(e => ({ ...e, state: 'searched' as const })),
			...excludedExperiences.map(e => ({ ...e, state: 'excluded' as const })),
		];

		const groups = {
			required: allExperiences.filter(e => e.state === 'required'),
			searched: allExperiences.filter(e => e.state === 'searched'),
			excluded: allExperiences.filter(e => e.state === 'excluded'),
		};

		return (
			<div className="space-y-1">
				{renderDropZone(
					'required', groups.required, 'experience',
					t("advancedSearch.experiences.required"), 'text-emerald-600 dark:text-emerald-400', false
				)}
				{renderDropZone(
					'searched', groups.searched, 'experience',
					t("advancedSearch.experiences.searched"), 'text-blue-500 dark:text-blue-400', true
				)}
				{renderDropZone(
					'excluded', groups.excluded, 'experience',
					t("advancedSearch.experiences.excluded"), 'text-red-500 dark:text-red-400', true
				)}
			</div>
		);
	};

	const renderChip = (item: (Skill | Experience) & { state: 'required' | 'searched' | 'excluded' }, type: 'skill' | 'experience') => {
		const isExperience = type === 'experience';
		const category = isExperience ? (item as Experience & { state: string }).category : null;

		return (
			<TooltipProvider key={item.id} delayDuration={200}>
				<Tooltip>
					<TooltipTrigger asChild>
						<Badge
							variant="outline"
							draggable
							onDragStart={(e) => handleDragStart(e, item.id, type)}
							onDragEnd={handleDragEnd}
							className={`cursor-grab active:cursor-grabbing select-none transition-all hover:shadow-sm flex items-center gap-1.5 pr-1 ${getChipClasses(item.state)} ${recentlyAddedSkills.has(item.id) || recentlyAddedExperiences.has(item.id) ? 'ring-2 ring-emerald-400 ring-offset-1' : ''}`}
							onClick={() => type === 'skill' ? cycleSkillState(item.id) : cycleExperienceState(item.id)}
						>
							<span className={`h-2 w-2 rounded-full flex-shrink-0 ${getStateColor(item.state)}`} />
								{item.name}
							{'isSoft' in item && item.isSoft && (
								<span className="text-[10px] opacity-50">{t("advancedSearch.skills.soft")}</span>
							)}
							{category && (
								<span className="text-[10px] opacity-50">({category.replace(/_/g, ' ')})</span>
							)}
							<button
								onClick={(e) => {
									e.stopPropagation();
									type === 'skill' ? removeSkill(item.id) : removeExperience(item.id);
								}}
								className="ml-0.5 hover:bg-black/10 dark:hover:bg-white/10 rounded-sm p-0.5"
							>
								<X className="h-3 w-3" />
							</button>
						</Badge>
					</TooltipTrigger>
					<TooltipContent side="top" className="text-xs">
						{t("advancedSearch.chipTooltip")}
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	};

	// Config view
	const renderConfigView = () => (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center gap-3 pb-4 border-b border-border dark:border-border">
				<div className="p-2 rounded-sm bg-blue-500">
					<Search className="h-5 w-5 text-white" />
				</div>
				<div className="flex-1">
					<h2 className="text-lg font-semibold text-foreground">
						{t("advancedSearch.title")}
						{jobTitle && companyName && (
							<span className="text-muted-foreground font-normal">{t("advancedSearch.jobAtCompany", { jobTitle, companyName })}</span>
						)}
					</h2>
					<p className="text-xs text-muted-foreground">{t("advancedSearch.subtitle")}</p>
				</div>
			</div>

			{/* Legend */}
			<div className="flex items-center gap-4 py-3 text-xs text-muted-foreground">
				<span className="flex items-center gap-1.5">
					<span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />
					{t("advancedSearch.legend.required")}
				</span>
				<span className="flex items-center gap-1.5">
					<span className="h-2.5 w-2.5 rounded-sm bg-blue-500" />
					{t("advancedSearch.legend.searched")}
				</span>
				<span className="flex items-center gap-1.5">
					<span className="h-2.5 w-2.5 rounded-sm bg-red-400" />
					{t("advancedSearch.legend.excluded")}
				</span>
				<span className="text-muted-foreground/60">— {t("advancedSearch.legend.clickToChange")}</span>
			</div>

			{/* Search Configuration */}
			<div className="flex-1 overflow-y-auto scrollbar-thin">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					{/* Skills Section */}
					<Card className="card-glass border-border dark:border-border">
						<CardHeader className="pb-3">
							<div className="flex items-center justify-between">
								<CardTitle className="text-sm font-medium text-foreground">{t("advancedSearch.skills.title")}</CardTitle>
								<div className="flex items-center gap-2">
									<input
										type="checkbox"
										id="soft-skills"
										checked={excludeSoftSkills}
										onChange={(e) => setExcludeSoftSkills(e.target.checked)}
										className="h-3.5 w-3.5 rounded"
									/>
									<Label htmlFor="soft-skills" className="cursor-pointer text-xs text-muted-foreground">
										{t("advancedSearch.skills.excludeSoft")}
									</Label>
								</div>
							</div>
						</CardHeader>
						<CardContent className="space-y-3 pt-0">
							{/* Quick Add Search */}
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
								<Input
									placeholder={t("advancedSearch.skills.searchPlaceholder")}
									value={skillSearchQuery}
									onChange={(e) => setSkillSearchQuery(e.target.value)}
									className="pl-9 h-8 text-sm"
								/>
								{isSearchingSkills && (
									<Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
								)}
								{skillSearchResults.length > 0 && (
									<div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-sm shadow-lg p-1.5 z-20 max-h-48 overflow-y-auto">
										{skillSearchResults.map((skill) => {
											const isAdded = recentlyAddedSkills.has(skill.id);
											return (
												<div
													key={skill.id}
													className={`px-2 py-1.5 hover:bg-accent rounded cursor-pointer text-sm flex items-center justify-between transition-colors ${
														isAdded ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''
													}`}
													onClick={() => addSkillToSection(skill, 'available')}
												>
													<div className="flex items-center gap-2">
														{isAdded && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
														<span className={isAdded ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-foreground'}>{skill.name}</span>
													</div>
													{skill.isSoft && <Badge variant="outline" className="text-[10px] h-5">{t("advancedSearch.skills.soft")}</Badge>}
												</div>
											);
										})}
									</div>
								)}
							</div>

							{/* Chips */}
							{renderSkillChips()}
						</CardContent>
					</Card>

					{/* Experiences Section */}
					<Card className="card-glass border-border dark:border-border">
						<CardHeader className="pb-3">
							<CardTitle className="text-sm font-medium text-foreground">{t("advancedSearch.experiences.title")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 pt-0">
							{/* Quick Add Search */}
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
								<Input
									placeholder={t("advancedSearch.experiences.searchPlaceholder")}
									value={experienceSearchQuery}
									onChange={(e) => setExperienceSearchQuery(e.target.value)}
									className="pl-9 h-8 text-sm"
								/>
								{isSearchingExperiences && (
									<Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
								)}
								{experienceSearchResults.length > 0 && (
									<div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-sm shadow-lg p-1.5 z-20 max-h-48 overflow-y-auto">
										{experienceSearchResults.map((exp) => {
											const isAdded = recentlyAddedExperiences.has(exp.id);
											return (
												<div
													key={exp.id}
													className={`px-2 py-1.5 hover:bg-accent rounded cursor-pointer text-sm flex items-center justify-between transition-colors ${
														isAdded ? 'bg-emerald-50 dark:bg-emerald-950/30' : ''
													}`}
													onClick={() => addExperienceToSection(exp, 'available')}
												>
													<div className="flex items-center gap-2">
														{isAdded && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
														<span className={isAdded ? 'text-emerald-700 dark:text-emerald-400 font-medium' : 'text-foreground'}>{exp.name}</span>
													</div>
													<Badge variant="outline" className="text-[10px] h-5">{exp.category.replace(/_/g, ' ')}</Badge>
												</div>
											);
										})}
									</div>
								)}
							</div>

							{/* Chips */}
							{renderExperienceChips()}
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Language Requirements Section */}
			<div className="mt-4">
				<Card className="card-glass border-border dark:border-border">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-medium text-foreground">{t("advancedSearch.languages.title")}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 pt-0">
						{languageOverrides.length === 0 && (
							<p className="text-xs text-muted-foreground">{t("advancedSearch.languages.noLanguages")}</p>
						)}
						{languageOverrides.map((lang, idx) => (
							<div key={lang.languageCode} className={`flex items-center gap-2 ${lang.excluded ? 'opacity-40' : ''}`}>
								<span className="text-xs font-mono w-6 text-center uppercase text-foreground">{lang.languageCode}</span>
								<span className="text-xs text-muted-foreground w-20 truncate">
									{COMMON_LANGUAGES[lang.languageCode as keyof typeof COMMON_LANGUAGES] ?? lang.languageCode}
								</span>

								{/* CEFR level selector */}
								<select
									value={lang.minCefrLevel}
									disabled={lang.excluded}
									onChange={(e) => {
										const updated = [...languageOverrides];
										updated[idx] = { ...updated[idx], minCefrLevel: parseInt(e.target.value) };
										setLanguageOverrides(updated);
									}}
									className="h-6 text-xs border border-border rounded bg-background text-foreground px-1 disabled:opacity-40"
								>
									{Object.entries(CEFR_LEVELS).map(([val, label]) => (
										<option key={val} value={val}>{label}</option>
									))}
								</select>

								{/* Mandatory toggle */}
								<button
									disabled={lang.excluded}
									onClick={() => {
										const updated = [...languageOverrides];
										updated[idx] = { ...updated[idx], isMandatory: !updated[idx].isMandatory };
										setLanguageOverrides(updated);
									}}
									className={`text-[10px] px-2 h-5 rounded border transition-colors disabled:opacity-40 ${
										lang.isMandatory
											? 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700'
											: 'bg-gray-50 dark:bg-gray-900 text-muted-foreground border-border'
									}`}
								>
									{lang.isMandatory ? t("advancedSearch.languages.mandatory") : t("advancedSearch.languages.optional")}
								</button>

								{/* Exclude toggle */}
								<button
									onClick={() => {
										const updated = [...languageOverrides];
										updated[idx] = { ...updated[idx], excluded: !updated[idx].excluded };
										setLanguageOverrides(updated);
									}}
									className={`text-[10px] px-2 h-5 rounded border ml-auto transition-colors ${
										lang.excluded
											? 'bg-red-400 text-white border-red-400'
											: 'text-muted-foreground border-border hover:text-red-500 hover:border-red-400'
									}`}
								>
									{lang.excluded ? t("advancedSearch.languages.excluded") : t("advancedSearch.languages.exclude")}
								</button>
							</div>
						))}

						{/* Add language row */}
						<div className="flex items-center gap-2 pt-1 border-t border-border/50 mt-2">
							<select
								value={newLanguageCode}
								onChange={(e) => setNewLanguageCode(e.target.value)}
								className="h-6 text-xs border border-border rounded bg-background text-foreground px-1 flex-1"
							>
								<option value="">{t("advancedSearch.languages.addPlaceholder")}</option>
								{Object.entries(COMMON_LANGUAGES)
									.filter(([code]) => !languageOverrides.some(o => o.languageCode === code))
									.map(([code, name]) => (
										<option key={code} value={code}>{name}</option>
									))
								}
							</select>
							<button
								disabled={!newLanguageCode}
								onClick={() => {
									if (!newLanguageCode) return;
									setLanguageOverrides(prev => [...prev, {
										languageCode: newLanguageCode,
										minCefrLevel: 3,
										isMandatory: false,
										excluded: false,
									}]);
									setNewLanguageCode('');
								}}
								className="text-[10px] px-2 h-6 rounded border border-blue-400 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 disabled:opacity-40 transition-colors"
							>
								{t("advancedSearch.languages.add")}
							</button>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Sticky Action Bar */}
			<div className="flex items-center gap-3 pt-4 border-t border-border dark:border-border mt-4">
				<Button
					onClick={() => executeSearch()}
					disabled={isSearching}
					className="flex-1 bg-blue-500 hover:bg-blue-500 text-white"
					size="lg"
				>
					{isSearching ? (
						<>
							<Loader2 className="h-4 w-4 mr-2 animate-spin" />
							{t("advancedSearch.actions.searching")}
						</>
					) : (
						<>
							<Search className="h-4 w-4 mr-2" />
							{t("advancedSearch.actions.findMatches")}
						</>
					)}
				</Button>
				{onClose && (
					<Button variant="outline" onClick={onClose} size="lg">
						<X className="h-4 w-4 mr-2" />
						{t("advancedSearch.actions.cancel")}
					</Button>
				)}
			</div>
		</div>
	);

	// Results view (includes loading/progress state)
	const renderResultsView = () => (
		<div className="flex flex-col h-full">
			{/* Results Header */}
			<div className="flex items-center justify-between pb-4 border-b border-border dark:border-border">
				<div className="flex items-center gap-3">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setCurrentView('config')}
						className="text-muted-foreground hover:text-foreground"
					>
						<ArrowLeft className="h-4 w-4 mr-1" />
						{t("advancedSearch.actions.backToCriteria")}
					</Button>
					<div className="h-4 w-px bg-border" />
					<h2 className="text-lg font-semibold text-foreground">
						{t("advancedSearch.results.title")}
					</h2>
					{searchResults?.shortlist && (
						<span className="text-sm text-muted-foreground">
							{t("advancedSearch.results.foundCount", { count: searchResults.shortlist.length })}
						</span>
					)}
				</div>
			</div>

			{/* Content area */}
			<div className="flex-1 overflow-y-auto scrollbar-thin pt-4">
				{/* Progress display while searching */}
				{isSearching && (
					<div className="flex flex-col items-center justify-center py-16">
						{/* Animated brain/filter/zap composition */}
						<div className="relative mb-8">
							{/* Outer pulsing ring */}
							<div className="absolute inset-0 -m-4 rounded-full border-2 border-blue-300/40 dark:border-blue-600/30 animate-ping" style={{ animationDuration: '3s' }} />
							<div className="absolute inset-0 -m-2 rounded-full border border-blue-200/50 dark:border-blue-700/30 animate-pulse" style={{ animationDuration: '2s' }} />

							{/* Central brain */}
							<div className="h-24 w-24 rounded-full bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center shadow-lg shadow-blue-200/50 dark:shadow-blue-900/30">
								<Brain className="h-12 w-12 text-blue-600 dark:text-blue-400 animate-pulse" />
							</div>

							{/* Orbiting filter icon */}
							<div
								className="absolute h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-950/50 flex items-center justify-center shadow-md"
								style={{
									top: '-8px', right: '-12px',
									animation: 'bounce 2.5s ease-in-out infinite',
								}}
							>
								<Filter className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
							</div>

							{/* Orbiting zap icon */}
							<div
								className="absolute h-9 w-9 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center shadow-md"
								style={{
									bottom: '-4px', left: '-14px',
									animation: 'bounce 2s ease-in-out infinite 0.5s',
								}}
							>
								<Zap className="h-5 w-5 text-amber-500 dark:text-amber-400" />
							</div>

							{/* Orbiting search icon */}
							<div
								className="absolute h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-950/50 flex items-center justify-center shadow-md"
								style={{
									top: '50%', right: '-20px', marginTop: '-16px',
									animation: 'bounce 3s ease-in-out infinite 1s',
								}}
							>
								<Search className="h-4 w-4 text-purple-600 dark:text-purple-400" />
							</div>
						</div>

						<p className="text-base font-semibold text-foreground mb-1">{t("advancedSearch.progress.analyzing")}</p>
						<p className="text-xs text-muted-foreground mb-6">{t("advancedSearch.progress.patience")}</p>
						{searchProgress.length > 0 && (
							<div className="w-full max-w-md space-y-1.5">
								{searchProgress
									.filter(progress => progress.status === 'completed')
									.map((progress) => (
										<div key={progress.step} className="flex items-start gap-2 text-xs text-muted-foreground">
											<CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
											<div className="flex-1 min-w-0">
												<span className="font-medium text-foreground">{t(`advancedSearch.progress.steps.${progress.step}`)}</span>
												{progress?.data && (
													<span className="text-muted-foreground ml-1">
														({Object.entries(progress.data).map(([key, value]) =>
															`${t(`advancedSearch.progress.metrics.${key}`, { defaultValue: key })}: ${value}`
														).join(' \u2022 ')})
													</span>
												)}
											</div>
										</div>
									))
								}
								<div ref={progressEndRef} />
							</div>
						)}
					</div>
				)}

				{/* Results list */}
				{!isSearching && searchResults && (() => {
					const scoreThreshold = flexibilityLevel === 'strict' ? 30 : flexibilityLevel === 'flexible' ? 10 : 20;
					const filteredResults = {
						...searchResults,
						shortlist: searchResults.shortlist?.filter((c: any) => c.combinedScore >= scoreThreshold) ?? [],
					};
					return (
						<SearchResultsList
							results={filteredResults}
							jobId={jobId}
							onAddMatches={onAddMatches}
							onClose={onClose}
							flexibilityLevel={flexibilityLevel}
							onFlexibilityChange={setFlexibilityLevel}
						/>
					);
				})()}
			</div>
		</div>
	);

	return currentView === 'config' ? renderConfigView() : renderResultsView();
}
